import { Debug, SymbolType } from "@spawn/common";
import { inject, service } from "@spawn/core";
import { GoldenBonusAssetsService, GoldenBonusSymbolTypes } from "@spawn/golden-bonus";
import { NetworkService, ReelController } from "@spawn/slot-machine";
import { NrgLaglessGenerator } from "../lagless/customLaglessGenerator";
import { hasConsecutiveMysteries } from "../network/helpers";
import { IGameNetworkState } from "../network/networkState";

export enum BaseSymbolTypes {
	LV1 = "LV1",
	LV2 = "LV2",
	LV3 = "LV3",
	LV4 = "LV4",
	HV1 = "HV1",
	HV2 = "HV2",
	HV3 = "HV3",
	HV4 = "HV4",
	Top = "TOP",
	Mystery = "FLOWER",
	Scatter = "SCATTER",
	ScatterFG = "SCATTER_FG",
}

export type SymbolTypes = BaseSymbolTypes | GoldenBonusSymbolTypes;

/** Z-index of a symbol that must draw above its neighbours. */
const ELEVATED_LAYER_PRIORITY = 1000;

export const symbolTypeCubeIds = new Map<string, SymbolTypes>([
	["LV1", BaseSymbolTypes.LV1],
	["LV2", BaseSymbolTypes.LV2],
	["LV3", BaseSymbolTypes.LV3],
	["LV4", BaseSymbolTypes.LV4],
	["HV1", BaseSymbolTypes.HV1],
	["HV2", BaseSymbolTypes.HV2],
	["HV3", BaseSymbolTypes.HV3],
	["HV4", BaseSymbolTypes.HV4],
	["TOP", BaseSymbolTypes.Top],
	["FLOWER", BaseSymbolTypes.Mystery],
	["SCATTER", BaseSymbolTypes.Scatter],
	["SCATTERFG", BaseSymbolTypes.ScatterFG],
	["CALAVERA_X1", GoldenBonusSymbolTypes.BonusX1],
	["CALAVERA_X2", GoldenBonusSymbolTypes.BonusX2],
	["CALAVERA_X5", GoldenBonusSymbolTypes.BonusX5],
	["CALAVERA_X10", GoldenBonusSymbolTypes.BonusX10],
	["CALAVERA_X25", GoldenBonusSymbolTypes.BonusX25],
	["CALAVERA_X50", GoldenBonusSymbolTypes.BonusX50],
	["CALAVERA_X100", GoldenBonusSymbolTypes.BonusX100],
	["CALAVERA_X250", GoldenBonusSymbolTypes.BonusX250],
	["CALAVERA_X500", GoldenBonusSymbolTypes.BonusX500],
	["CALAVERA_X1000", GoldenBonusSymbolTypes.BonusX1000],
	["CALAVERA_X2500", GoldenBonusSymbolTypes.BonusX2500],
]);

export const symbolTypeBraggIds = new Map<number, SymbolTypes | string>([
	[1, BaseSymbolTypes.LV4],
	[2, BaseSymbolTypes.LV3],
	[3, BaseSymbolTypes.LV2],
	[4, BaseSymbolTypes.LV1],
	[5, BaseSymbolTypes.HV4],
	[6, BaseSymbolTypes.HV3],
	[7, BaseSymbolTypes.HV2],
	[8, BaseSymbolTypes.HV1],
	[9, BaseSymbolTypes.Top],
	[10, BaseSymbolTypes.Scatter],
	[11, BaseSymbolTypes.Mystery],
	[12, "DUMMY"],
	[13, "DUMMY"],
	[14, "DUMMY"],
	[15, "DUMMY"],
	[16, "DUMMY"],
	[17, "DUMMY"],
	[18, "GOLDEN_BONUS"], // this could be causing the issues we sometimes get (mostly during fg)
	[19, GoldenBonusSymbolTypes.BonusX1],
	[20, GoldenBonusSymbolTypes.BonusX2],
	[21, GoldenBonusSymbolTypes.BonusX5],
	[22, GoldenBonusSymbolTypes.BonusX10],
	[23, GoldenBonusSymbolTypes.BonusX25],
	[24, GoldenBonusSymbolTypes.BonusX50],
	[25, GoldenBonusSymbolTypes.BonusX100],
	[26, GoldenBonusSymbolTypes.BonusX250],
	[27, GoldenBonusSymbolTypes.BonusX500],
	[28, GoldenBonusSymbolTypes.BonusX1000],
	[29, GoldenBonusSymbolTypes.BonusX2500],
	[30, BaseSymbolTypes.ScatterFG],
]);

/**
 * Symbol logic that is the same in all games. Each game extends this class with its asset
 * handles and its art (textures, animations, offsets), and registers the subclass with
 * `this.override(CoreSymbolService, SymbolService)`. Keep the name of the subclass
 * "SymbolService": params and assets are keyed by the class name.
 */
@service
export class SymbolService {
	@inject protected readonly goldenBonusAssetsService!: GoldenBonusAssetsService;
	@inject protected readonly laglessGenerator!: NrgLaglessGenerator;
	@inject protected readonly reelController!: ReelController;
	@inject protected readonly networkService!: NetworkService<IGameNetworkState>;

	public getDelayUntilRevealAnimation(symbolType: SymbolType): number | undefined {
		switch (symbolType) {
			case BaseSymbolTypes.Top:
			case BaseSymbolTypes.Scatter:
			case BaseSymbolTypes.ScatterFG:
			case GoldenBonusSymbolTypes.BonusX1:
			case GoldenBonusSymbolTypes.BonusX2:
			case GoldenBonusSymbolTypes.BonusX5:
			case GoldenBonusSymbolTypes.BonusX10:
			case GoldenBonusSymbolTypes.BonusX25:
			case GoldenBonusSymbolTypes.BonusX50:
			case GoldenBonusSymbolTypes.BonusX100:
			case GoldenBonusSymbolTypes.BonusX250:
			case GoldenBonusSymbolTypes.BonusX500:
			case GoldenBonusSymbolTypes.BonusX1000:
			case GoldenBonusSymbolTypes.BonusX2500:
				return 0.2;
			default:
				return 0.5;
		}
	}

	public getGoldenStopSoundIndex(symbolType: SymbolType): number | undefined {
		switch (symbolType) {
			case GoldenBonusSymbolTypes.BonusX1:
			case GoldenBonusSymbolTypes.BonusX2:
			case GoldenBonusSymbolTypes.BonusX5:
				return 0;
			case GoldenBonusSymbolTypes.BonusX10:
			case GoldenBonusSymbolTypes.BonusX25:
			case GoldenBonusSymbolTypes.BonusX50:
				return 1;
			case GoldenBonusSymbolTypes.BonusX100:
			case GoldenBonusSymbolTypes.BonusX250:
			case GoldenBonusSymbolTypes.BonusX500:
				return 2;
			case GoldenBonusSymbolTypes.BonusX1000:
			case GoldenBonusSymbolTypes.BonusX2500:
			case BaseSymbolTypes.Scatter:
			case BaseSymbolTypes.ScatterFG:
				return 3;
			default:
				return undefined;
		}
	}

	/**
	 * Symbol types that must draw above their neighbours, because their art spills into the
	 * cells around them. Every other symbol type gets DEFAULT_LAYER_PRIORITY.
	 */
	private readonly symbolLayerPriorities: Partial<Record<SymbolTypes, number>> = {
		[BaseSymbolTypes.Scatter]: ELEVATED_LAYER_PRIORITY,
		[BaseSymbolTypes.ScatterFG]: ELEVATED_LAYER_PRIORITY,
	};

	public getSymbolLayerPriority(symbolType: string): number | undefined {
		return this.symbolLayerPriorities[symbolType as SymbolTypes];
	}

	/**
	 * Replaces swing off (top) Mystery if there are at least 4 consecutive mysteries in visible symbols
	 * or if the first visible symbol is not a mystery
	 * This is to prevent the next spin from showing a non-complete 4 mystery block.
	 */
	public replaceSwingingMysteriesIfNotNeeded(reels: string[][]): void {
		for (const reel of reels) {
			const visibleSymbols = reel.slice(1, -1);

			if (
				reel[0] === BaseSymbolTypes.Mystery &&
				(reel[1] !== BaseSymbolTypes.Mystery || hasConsecutiveMysteries(visibleSymbols, 4))
			) {
				reel[0] = this.laglessGenerator.getRandomNonCriticalSymbols(1)[0];
			}
		}
	}

	public getScatterSymbolsCoords(): { x: number; y: number }[] {
		return [
			...this.reelController.getSymbolCoords(BaseSymbolTypes.Scatter),
			...this.reelController.getSymbolCoords(BaseSymbolTypes.ScatterFG),
			...((this.networkService.state as IGameNetworkState).reveal?.symbols
				?.filter((s) => [BaseSymbolTypes.Scatter, BaseSymbolTypes.ScatterFG].includes(s.symbol as BaseSymbolTypes))
				.map((s) => s.position) ?? []),
		];
	}
}

export function convertCubeIdToSymbolType(cubeId: string): SymbolType {
	const symbolType = symbolTypeCubeIds.get(cubeId);
	Debug.assertIsDefined(symbolType, `No symbol type name associated with the specified cube symbol type id "${cubeId}".`);
	return symbolType;
}

export function convertBraggIdToSymbolTypeId(braggId: number): SymbolType {
	const symbolType = symbolTypeBraggIds.get(braggId);
	Debug.assertIsDefined(symbolType, `No symbol type name associated with the specified bragg symbol type id "${braggId}".`);
	return symbolType;
}
